export const APPLY_WHT = true;
export const PCT_WHT = 0.03;
export const PCT_DEFAULT_SHOP = 0.30;
export const PCT_SPECIAL_SHOP = 0.25;
export const PCT_LITTLE_SHOP = 0.15;

// ชื่อ canonical ที่ใช้จริงในระบบ
// รองรับ alias เก่าที่เคยถูกบันทึกไว้ เพื่อไม่ให้รายงานแตกเป็นคนละทีม
export const TEAM_NAME_ALIASES = {
  "บอตอ": "บต",
  "แฟนยย": "ลิตเติ้ล",
};

export const normalizeName = (value) => String(value || "").trim();
export const canonicalTeamName = (value) => {
  const normalized = normalizeName(value);
  return TEAM_NAME_ALIASES[normalized] || normalized;
};

// กติกาค่าคอมคิดตามชื่อทีมเท่านั้น ไม่แยกตามประเภทงานแล้ว
export const SPECIAL_25_NAMES = ["เฟย", "บต", "ม้าค"];
export const ALWAYS_WHT_EXEMPT_NAMES = ["ยย"];
export const SALARY_WHT_EXEMPT_NAMES = ["จจ", "เฟย", "กอ", "บต"];
export const NO_SHOP_DEDUCT_NAMES = ["ยย"];

// พนักงานเงินเดือน: รวมโอนค่ากดทุกวันที่ 26 ของเดือน
// วันที่ 1–25 = รอบโอนวันที่ 26 ของเดือนนั้น
// วันที่ 26–สิ้นเดือน = ยังบันทึกค่ากดได้ตามปกติ แต่เลื่อนไปรอบโอนวันที่ 26 ของเดือนถัดไป
// สำคัญ: cutoff นี้มีผลเฉพาะ “รอบโอน” ไม่ได้เลื่อนรายได้/ค่าคอม/กำไรไปเดือนถัดไป
export const SALARIED_TEAM_NAMES = ["จจ", "กอ", "บต", "เฟย"];
export const SALARY_ENTRY_CUTOFF_DAY = 25;

// พนักงานเงินเดือน จจ / กอ / บต / เฟย เริ่มไม่หัก ณ ที่จ่ายตั้งแต่ 1 ก.ค. 2569
// รายการก่อนหน้านั้นยังใช้กติกาเดิมและหัก ณ ที่จ่าย 3%
export const SALARY_WHT_EXEMPT_START = { year: 2026, month: 7, day: 1 };

// ลิตเติ้ลเริ่มถูกหักค่าคอม 15% ตั้งแต่ 1 ต.ค. 2569 เป็นต้นไป
export const LITTLE_COMMISSION_START = { year: 2026, month: 10, day: 1 };


const toDate = (value) => {
  if (!value) return new Date();
  if (value?.toDate) return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

// ใช้วันตามเวลาไทยเสมอ เพื่อให้ cutoff วันที่ 25/26 ไม่เพี้ยนตาม timezone ของเครื่อง
export const getBangkokDateParts = (value = new Date()) => {
  const date = toDate(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const read = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
  return { year: read("year"), month: read("month"), day: read("day") };
};

const isOnOrAfter = (parts, target) => {
  const currentKey = parts.year * 10000 + parts.month * 100 + parts.day;
  const targetKey = target.year * 10000 + target.month * 100 + target.day;
  return currentKey >= targetKey;
};

export const isSpecial25 = (name) => SPECIAL_25_NAMES.includes(canonicalTeamName(name));
export const isSalariedTeam = (name) => SALARIED_TEAM_NAMES.includes(canonicalTeamName(name));

// คงฟังก์ชันนี้ไว้เพื่อ compatibility กับโค้ดเก่า แต่ปัจจุบัน “ไม่ล็อกการกรอก” หลังวันที่ 25 แล้ว
// รายการหลัง cutoff จะถูกจัดรอบจ่ายใน PaymentStatus แทน
export const isSalaryEntryLocked = () => false;

export const getShopPctForTeam = (teamName, at = new Date()) => {
  const normalized = canonicalTeamName(teamName);
  if (NO_SHOP_DEDUCT_NAMES.includes(normalized)) return 0;

  // ลิตเติ้ล: ก่อน 1 ต.ค. 2569 ไม่หักค่าคอม, ตั้งแต่ 1 ต.ค. 2569 หัก 15%
  if (normalized === "ลิตเติ้ล") {
    const parts = getBangkokDateParts(at);
    return isOnOrAfter(parts, LITTLE_COMMISSION_START) ? PCT_LITTLE_SHOP : 0;
  }

  if (isSpecial25(normalized)) return PCT_SPECIAL_SHOP;
  return PCT_DEFAULT_SHOP;
};

export const isWhtExempt = (teamName, at = new Date()) => {
  const normalized = canonicalTeamName(teamName);
  if (ALWAYS_WHT_EXEMPT_NAMES.includes(normalized)) return true;

  if (SALARY_WHT_EXEMPT_NAMES.includes(normalized)) {
    const parts = getBangkokDateParts(at);
    return isOnOrAfter(parts, SALARY_WHT_EXEMPT_START);
  }

  return false;
};

export const shouldApplyWhtForTeam = (teamName, at = new Date()) =>
  APPLY_WHT && !isWhtExempt(teamName, at);

export const calculateFeeAmounts = ({
  teamName,
  feePerTicket = 0,
  quantity = 0,
  totalFee,
  effectiveAt = new Date(),
} = {}) => {
  const qty = Number(quantity || 0);
  const feePer = Number(feePerTicket || 0);
  const baseFee = Number(totalFee || 0) || feePer * qty;
  const shopPct = getShopPctForTeam(teamName, effectiveAt);
  const deducted = baseFee * shopPct;
  const teamBeforeTax = baseFee - deducted;
  const applyWht = shouldApplyWhtForTeam(teamName, effectiveAt);
  const tax = applyWht ? teamBeforeTax * PCT_WHT : 0;
  const net = teamBeforeTax - tax;

  return {
    qty,
    feePer,
    baseFee,
    shopPct,
    deducted,
    totalCompCut: deducted,
    teamBeforeTax,
    beforeTax: teamBeforeTax,
    applyWht,
    tax,
    wht: tax,
    net,
  };
};

// อ่านตัวเลขที่เคยบันทึกไว้ใน record โดยให้ค่า 0 ถือว่าเป็นค่าจริงด้วย
// รองรับชื่อ field เก่าบางแบบ เผื่อมีข้อมูลหลายเวอร์ชันใน Firestore
const readStoredNumber = (record, keys = []) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value === null || value === undefined || value === "") continue;
    const number = Number(value);
    if (Number.isFinite(number)) return { found: true, value: number, key };
  }
  return { found: false, value: 0, key: null };
};

const normalizeStoredRate = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  // เผื่อข้อมูลเก่าเก็บ 25 แทน 0.25
  return number > 1 ? number / 100 : number;
};

// สำคัญ: record ที่บันทึกลง Firestore แล้วถือว่าเป็น snapshot ของกติกา ณ วันนั้น
// หน้าประวัติ/รายงาน/โอน/อันดับจึงต้องใช้ยอดที่เก็บไว้ใน database ก่อนเสมอ
// และค่อย fallback ไปคำนวณจาก rule ปัจจุบันเฉพาะ record เก่ามากที่ไม่มี field ยอดเหล่านี้จริง ๆ
export const calculateRecordFee = (record = {}) => {
  const fallback = calculateFeeAmounts({
    teamName: canonicalTeamName(record.teamName),
    feePerTicket: record.feePerTicket,
    quantity: record.quantity,
    totalFee: record.totalFee,
    effectiveAt: record.createdAt || record.date || record.pressDate || new Date(),
  });

  const storedBase = readStoredNumber(record, ["totalFee", "baseFee"]);
  const storedDeducted = readStoredNumber(record, ["totalDeducted", "deducted", "shopCut"]);
  const storedBeforeTax = readStoredNumber(record, ["teamBeforeTax", "beforeTax"]);
  const storedTax = readStoredNumber(record, ["withholding3pct", "tax", "wht"]);
  const storedNet = readStoredNumber(record, ["netIncome", "net"]);
  const storedRate = readStoredNumber(record, ["commissionRate", "shopPct"]);

  const baseFee = storedBase.found ? storedBase.value : fallback.baseFee;

  let shopPct = storedRate.found ? normalizeStoredRate(storedRate.value) : null;
  let deducted;
  if (storedDeducted.found) {
    deducted = storedDeducted.value;
  } else if (shopPct !== null) {
    deducted = baseFee * shopPct;
  } else {
    deducted = fallback.deducted;
  }

  // ถ้า database มีจำนวนเงินที่หักจริง แต่ไม่มี rate ให้หา rate จากยอดจริงแทน
  if (shopPct === null) {
    shopPct = baseFee > 0 ? deducted / baseFee : fallback.shopPct;
  }

  let teamBeforeTax = storedBeforeTax.found
    ? storedBeforeTax.value
    : baseFee - deducted;

  let tax;
  let net;

  if (storedTax.found && storedNet.found) {
    // กรณีปกติของ record ที่ระบบเคยบันทึกไว้: ใช้ database ตรง ๆ
    tax = storedTax.value;
    net = storedNet.value;
  } else if (storedNet.found) {
    // มีสุทธิ แต่ไม่มียอดหัก ณ ที่จ่าย → ย้อนจากตัวเลขที่บันทึกไว้
    net = storedNet.value;
    tax = Math.max(0, teamBeforeTax - net);
  } else if (storedTax.found) {
    // มีหัก ณ ที่จ่าย แต่ไม่มีสุทธิ → คำนวณจากยอดที่บันทึกไว้เท่านั้น
    tax = storedTax.value;
    net = teamBeforeTax - tax;
  } else if (typeof record.applyWht === "boolean") {
    // record รุ่นเก่าที่มี flag แต่ยังไม่มียอด tax/net
    tax = record.applyWht ? teamBeforeTax * PCT_WHT : 0;
    net = teamBeforeTax - tax;
  } else {
    // fallback สุดท้ายสำหรับข้อมูลเก่ามากที่ไม่มี snapshot การคำนวณเลย
    tax = fallback.tax;
    net = teamBeforeTax - tax;
  }

  let applyWht = typeof record.applyWht === "boolean"
    ? record.applyWht
    : tax > 0
      ? true
      : (storedTax.found || storedNet.found ? false : fallback.applyWht);

  // กติกาที่มีวันเริ่มชัดเจนต้อง override snapshot ที่ถูกบันทึกผิดในช่วงเปลี่ยนระบบ
  // จจ / กอ / บต / เฟย: ตั้งแต่ 1 ก.ค. 2569 เป็นต้นไป "ไม่หัก ณ ที่จ่าย"
  // ก่อนวันดังกล่าวยังคงยึดตัวเลขเดิมใน database ตามที่บันทึกไว้
  const recordEffectiveAt = record.createdAt || record.date || record.pressDate || new Date();
  const normalizedTeamName = canonicalTeamName(record.teamName);
  const recordDateParts = getBangkokDateParts(recordEffectiveAt);
  const salaryWhtExemptNow =
    SALARY_WHT_EXEMPT_NAMES.includes(normalizedTeamName) &&
    isOnOrAfter(recordDateParts, SALARY_WHT_EXEMPT_START);

  // ลิตเติ้ล: ตั้งแต่ 1 ต.ค. 2569 ต้องใช้ค่าคอม 15% เสมอ
  // override snapshot 25% ที่อาจถูกบันทึกไว้ก่อนแก้กติกา เพื่อให้หน้าโอน/รายงานคำนวณถูกต้อง
  const littleCommission15Now =
    normalizedTeamName === "ลิตเติ้ล" &&
    isOnOrAfter(recordDateParts, LITTLE_COMMISSION_START);

  if (littleCommission15Now) {
    shopPct = PCT_LITTLE_SHOP;
    deducted = baseFee * shopPct;
    teamBeforeTax = baseFee - deducted;
    applyWht = shouldApplyWhtForTeam(normalizedTeamName, recordEffectiveAt);
    tax = applyWht ? teamBeforeTax * PCT_WHT : 0;
    net = teamBeforeTax - tax;
  }

  if (salaryWhtExemptNow) {
    tax = 0;
    net = teamBeforeTax;
    applyWht = false;
  }

  return {
    qty: Number(record.quantity || fallback.qty || 0),
    feePer: Number(record.feePerTicket || fallback.feePer || 0),
    baseFee,
    shopPct,
    deducted,
    totalCompCut: deducted,
    teamBeforeTax,
    beforeTax: teamBeforeTax,
    applyWht,
    tax,
    wht: tax,
    net,
    // ใช้ตรวจ/debug ได้ว่ารายการนี้อ่านยอดเก่าจาก database หรือ fallback คำนวณใหม่
    usesStoredAmounts: storedDeducted.found || storedBeforeTax.found || storedTax.found || storedNet.found,
  };
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

// ใช้เฉพาะตอนแอดมินแก้ค่ากดของโซนเดิมย้อนหลัง
// หลักการคือเปลี่ยนฐานค่ากด แต่คง "อัตราค่าคอม + วิธีหัก ณ ที่จ่าย" ของ record เดิมไว้
// เพื่อไม่เอากติกาปัจจุบันไปทับประวัติเดือนเก่า
export const recalculateRecordAfterEventFeeChange = (record = {}, newFeePerTicket = 0) => {
  const snapshot = calculateRecordFee(record);
  const quantity = Number(record.quantity || snapshot.qty || 0);
  const feePerTicket = Number(newFeePerTicket || 0);
  const totalFee = roundMoney(feePerTicket * quantity);

  const commissionRate = Number.isFinite(Number(snapshot.shopPct))
    ? Number(snapshot.shopPct)
    : 0;
  const totalDeducted = roundMoney(totalFee * commissionRate);
  const teamBeforeTax = roundMoney(totalFee - totalDeducted);

  // ใช้อัตรา WHT ที่ record เดิม "มีผลจริง" หลังผ่าน historical override แล้ว
  // เช่น จจ/กอ/บต/เฟย หลัง 1 ก.ค. 2569 จะได้ 0% แม้ snapshot เก่าเคยบันทึกผิด
  const whtRate = snapshot.teamBeforeTax > 0
    ? Math.max(0, Number(snapshot.tax || 0) / Number(snapshot.teamBeforeTax || 1))
    : (snapshot.applyWht ? PCT_WHT : 0);
  const withholding3pct = roundMoney(teamBeforeTax * whtRate);
  const netIncome = roundMoney(teamBeforeTax - withholding3pct);

  return {
    feePerTicket,
    totalFee,
    commissionRate,
    totalDeducted,
    teamBeforeTax,
    applyWht: withholding3pct > 0,
    withholding3pct,
    netIncome,
    calculationSnapshot: true,
  };
};

